import {useEffect, useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useParams} from 'react-router-dom'
import {DictionaryService} from '../service/dictionary.services'
import {setTranslationCache as setGlobalTranslationCache} from './getTranslation'
import {resolveTranslation} from './translation.functions'
import {LOCAL_TRANSLATIONS, DEFAULT_LANGUAGE} from '../i18n/localDictionary'
import {TDictionary} from '../types/dictionary.types'

export function useTranslation() {
  const {clientId} = useParams()
  const language = window.navigator.language.slice(0, 2) || 'fr'
  const isTranslationChanged =
    localStorage.getItem('translation-change') === 'true'

  const {
    data: rawData,
    isLoading,
    isError,
  } = useQuery<TDictionary[]>({
    queryKey: ['translations', clientId, language],
    queryFn: async () => {
      const result = await DictionaryService.getDictionariesByClient(
        clientId as string,
      )
      localStorage.setItem('translation-change', 'false')
      return result as TDictionary[]
    },
    enabled: !!clientId,
    staleTime: isTranslationChanged ? 0 : Infinity,
    gcTime: Infinity,
  })

  const translations = useMemo<Record<string, string>>(() => {
    if (!rawData) return {}
    return Object.fromEntries(
      rawData.map(dict => [
        dict.key,
        (dict[language as 'en' | 'fr'] as string) ?? dict.key,
      ]),
    )
  }, [rawData, language])

  // Sync React Query cache into the imperative getTranslation() helper
  useEffect(() => {
    if (rawData && rawData.length > 0) {
      setGlobalTranslationCache(language, translations)
    }
  }, [translations, language, rawData])

  const localFallback =
    LOCAL_TRANSLATIONS[language] ?? LOCAL_TRANSLATIONS[DEFAULT_LANGUAGE]

  // Prefer the API dictionary, fall back to the bundled local dictionary, then
  // to the raw key. Replaces each {{...}} placeholder with the matching p0 value.
  // Example: t('deleteMessage', ['Jean']) => "Voulez-vous vraiment supprimer le client Jean ?"
  const t = (key: string, p0?: (string | null)[] | null): string =>
    resolveTranslation(key, translations, localFallback, p0)

  return {t, loading: isLoading, error: isError ? 'Translation error' : null}
}
